import React, { useEffect, useRef, useState } from 'react';
import { User, Code, Brain, Award, BookOpen, Coffee, ArrowRight, Sun, Moon, Sparkles, MapPin, Calendar } from 'lucide-react';
import { motion, useMotionValue, useSpring } from 'framer-motion';
import { useTheme } from './src/ThemeContext.jsx';

/**
 * About Me — Standalone Page (Advanced 3D/hover UX)
 * - Matches homepage navbar and theming (light/dark + persistent toggle)
 * - 3D tilt + glow effects tuned for BOTH light and dark modes
 * - Parallax sparkles, magnetic buttons; respects reduced motion
 */

export default function AboutMePage() {
  const { darkMode, toggleTheme } = useTheme();

  return (
    <div className={darkMode ? 'min-h-screen bg-[#0a0c10] text-white' : 'min-h-screen bg-white text-gray-900'}>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] px-3 py-2 rounded-md bg-indigo-600 text-white">Skip to content</a>

      {/* NAV — same as homepage */}
      <header className="sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto mt-4 px-4 sm:px-6">
          <div className={`backdrop-blur rounded-2xl shadow-sm flex items-center justify-between px-4 sm:px-6 py-3 ${darkMode ? 'bg-white/5 border border-white/10' : 'bg-white/80 border border-white/90'}`}>
            <a href="/" className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-semibold text-white ${darkMode ? 'bg-gradient-to-br from-indigo-500 to-cyan-400' : 'bg-gradient-to-br from-indigo-600 to-cyan-500 drop-shadow'}`}>ST</div>
              <div className="leading-tight">
                <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Sikandar Tareen</div>
                <div className={`text-[11px] ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>Agentic AI Engineer</div>
              </div>
            </a>
            <div className={`hidden md:flex items-center gap-6 text-sm ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
              <a href="/" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Home</a>
              <a href="/#projects" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Projects</a>
              <a href="/blog" className={`${darkMode ? 'hover:text-white' : 'hover:text-black'}`}>Blog</a>
              <a href="/about-me" className={`${darkMode ? 'text-white' : 'text-black'} font-medium`}>About</a>

              <a href="/contact" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-blue-600 text-white">
                <Sparkles size={14} /> Contact Me
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* PARALLAX SPARKLES */}
      <ParallaxField darkMode={darkMode} />

      <main id="main" className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 pt-10 [perspective:1200px]">
        {/* Hero */}
        <section className="relative overflow-visible">
          <div className={`absolute -top-24 -left-24 w-[42rem] h-[42rem] rounded-full blur-3xl ${darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/60'}`}></div>
          <div className={`absolute -bottom-32 -right-16 w-[36rem] h-[36rem] rounded-full blur-3xl ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/60'}`}></div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative z-10">
            <h1 className="text-4xl sm:text-5xl font-extrabold">About Me</h1>
            <p className={`mt-3 text-lg max-w-2xl ${darkMode ? 'text-white/80' : 'text-gray-700'}`}>I'm an Agentic AI Engineer passionate about building intelligent systems that augment human capabilities. Here's my journey, skills, and what drives me to create cutting-edge AI solutions.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Magnetic>
                <a href="#journey" className={`px-5 py-3 rounded-xl font-medium inline-flex items-center gap-2 bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${
                  darkMode ? 'focus-visible:ring-cyan-300/60' : 'focus-visible:ring-indigo-500'
                }`}>
                  My Journey <ArrowRight size={16}/>
                </a>
              </Magnetic>
              <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/10 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-xl`}>
                <a href="/contact" className="block px-5 py-3">Work with me</a>
              </TiltCard>
            </div>
          </motion.div>
        </section>

        {/* Core Skills */}
        <section className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <Brain />, title: 'AI & Machine Learning', desc: 'Deep expertise in LLMs, agent architectures, and neural networks.' },
            { icon: <Code />, title: 'Software Engineering', desc: 'Full-stack development with focus on scalable, production-ready systems.' },
            { icon: <Award />, title: 'System Design', desc: 'Architecting robust, distributed systems for enterprise-scale applications.' },
          ].map((s) => (
            <TiltCard key={s.title} darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border focus-within:ring-2 ${darkMode ? 'focus-within:ring-cyan-300/50' : 'focus-within:ring-indigo-500'}`}>
              <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg w-fit`}>{s.icon}</div>
              <div className="mt-3 font-semibold">{s.title}</div>
              <div className={`${darkMode ? 'text-white/70' : 'text-gray-600'} text-sm mt-1`}>{s.desc}</div>
            </TiltCard>
          ))}
        </section>

        {/* Detailed Skills */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold">Technical Skills</h2>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* AI & Machine Learning */}
            <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg`}>
                  <Brain size={20} />
                </div>
                <h3 className="text-lg font-semibold">AI & Machine Learning</h3>
              </div>
              <div className="space-y-3">
                {[
                  { skill: 'Large Language Models', level: 95 },
                  { skill: 'Agent Architectures', level: 90 },
                  { skill: 'RAG Systems', level: 88 },
                  { skill: 'Neural Networks', level: 85 },
                  { skill: 'Computer Vision', level: 75 }
                ].map((item) => (
                  <div key={item.skill}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{item.skill}</span>
                      <span className={`${darkMode ? 'text-white/60' : 'text-gray-600'}`}>{item.level}%</span>
                    </div>
                    <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
                      <div className="bg-gradient-to-r from-indigo-600 to-cyan-500 h-2 rounded-full transition-all duration-500" style={{width: `${item.level}%`}}></div>
                    </div>
                  </div>
                ))}
              </div>
            </TiltCard>

            {/* Programming Languages */}
            <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg`}>
                  <Code size={20} />
                </div>
                <h3 className="text-lg font-semibold">Programming Languages</h3>
              </div>
              <div className="space-y-3">
                {[
                  { skill: 'Python', level: 95 },
                  { skill: 'JavaScript/TypeScript', level: 90 },
                  { skill: 'Go', level: 85 },
                  { skill: 'Rust', level: 75 },
                  { skill: 'SQL', level: 88 }
                ].map((item) => (
                  <div key={item.skill}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{item.skill}</span>
                      <span className={`${darkMode ? 'text-white/60' : 'text-gray-600'}`}>{item.level}%</span>
                    </div>
                    <div className={`w-full rounded-full h-2 ${darkMode ? 'bg-white/10' : 'bg-gray-200'}`}>
                      <div className="bg-gradient-to-r from-indigo-600 to-cyan-500 h-2 rounded-full transition-all duration-500" style={{width: `${item.level}%`}}></div>
                    </div>
                  </div>
                ))}
              </div>
            </TiltCard>

            {/* Frameworks & Tools */}
            <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg`}>
                  <Award size={20} />
                </div>
                <h3 className="text-lg font-semibold">Frameworks & Tools</h3>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  'LangChain', 'LlamaIndex', 'OpenAI API', 'Anthropic Claude',
                  'React/Next.js', 'Node.js', 'FastAPI', 'Docker',
                  'Kubernetes', 'AWS/GCP', 'PostgreSQL', 'Redis'
                ].map((tool) => (
                  <div key={tool} className={`${darkMode ? 'bg-white/5 text-white' : 'bg-gray-50 text-gray-800'} px-3 py-2 rounded-lg text-center`}>
                    {tool}
                  </div>
                ))}
              </div>
            </TiltCard>

            {/* Specializations */}
            <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`${darkMode ? 'bg-white/10 text-white' : 'bg-indigo-50 text-indigo-700'} p-2 rounded-lg`}>
                  <User size={20} />
                </div>
                <h3 className="text-lg font-semibold">Specializations</h3>
              </div>
              <div className="space-y-3">
                {[
                  { area: 'Multi-Agent Systems', desc: 'Orchestrating complex agent workflows' },
                  { area: 'AI Safety & Governance', desc: 'Implementing responsible AI practices' },
                  { area: 'Production AI Systems', desc: 'Scaling AI solutions for enterprise' },
                  { area: 'System Architecture', desc: 'Designing distributed, fault-tolerant systems' }
                ].map((spec) => (
                  <div key={spec.area} className={`${darkMode ? 'bg-white/5' : 'bg-gray-50'} p-3 rounded-lg`}>
                    <div className="font-medium">{spec.area}</div>
                    <div className={`text-sm ${darkMode ? 'text-white/70' : 'text-gray-600'} mt-1`}>{spec.desc}</div>
                  </div>
                ))}
              </div>
            </TiltCard>
          </div>
        </section>

        {/* Experience Timeline */}
        <section id="journey" className="mt-14">
          <h2 className="text-2xl font-bold">My Journey</h2>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[{
              period: '2020-2022', role: 'Software Engineer', company: 'Tech Startup', highlights: ['Built scalable web applications', 'Led frontend development team', 'Implemented CI/CD pipelines', 'Mentored junior developers']
            }, {
              period: '2022-2023', role: 'AI Research Engineer', company: 'AI Lab', highlights: ['Researched LLM architectures', 'Published 3 research papers', 'Developed novel training techniques', 'Collaborated with academia']
            }, {
              period: '2023-Present', role: 'Agentic AI Engineer', company: 'Freelance', highlights: ['Design multi-agent systems', 'Enterprise AI consulting', 'Production AI deployments', 'AI safety & governance']
            }].map((exp) => (
              <TiltCard key={exp.period} darkMode={darkMode} glow className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-6 border`}>
                <div className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-sm`}>{exp.period}</div>
                <div className="mt-1 text-xl font-bold">{exp.role}</div>
                <div className={`${darkMode ? 'text-white/80' : 'text-gray-700'} text-sm font-medium`}>{exp.company}</div>
                <ul className={`${darkMode ? 'text-white/70' : 'text-gray-600'} mt-3 text-sm space-y-1`}>
                  {exp.highlights.map(h => <li key={h}>• {h}</li>)}
                </ul>
              </TiltCard>
            ))}
          </div>
        </section>

        {/* Values & Approach */}
        <section className="mt-14">
          <h2 className="text-2xl font-bold">Values & Approach</h2>
          <ol className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            {['Innovation', 'Quality', 'Collaboration', 'Impact'].map((value, i) => (
              <TiltCard key={value} darkMode={darkMode} className={`${darkMode ? 'bg-white/5 border-white/10 text-white hover:shadow-cyan-900/30' : 'bg-white border-gray-200 text-gray-900 hover:shadow-indigo-200'} rounded-2xl p-4 border`}>
                <div className={`${darkMode ? 'text-white/60' : 'text-gray-600'} text-xs`}>Core Value {i+1}</div>
                <div className="font-semibold mt-1">{value}</div>
                <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} mt-1`}>{[
                  'Pushing boundaries with cutting-edge AI research and development.',
                  'Delivering robust, well-tested solutions that stand the test of time.',
                  'Working closely with teams to achieve shared goals and knowledge transfer.',
                  'Building AI systems that create meaningful value for users and society.',
                ][i]}</p>
              </TiltCard>
            ))}
          </ol>
        </section>

        {/* Personal Interests */}
        <section className="mt-16">
          <TiltCard darkMode={darkMode} glow className={`${darkMode ? 'from-indigo-600/30 to-cyan-500/30 border-white/10 text-white' : 'from-indigo-100 to-cyan-100 border-gray-200 text-gray-900'} rounded-3xl p-8 bg-gradient-to-r border`}>
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-2xl font-bold">Beyond the Code</h3>
                <p className={`${darkMode ? 'text-white/80' : 'text-gray-700'} mt-2`}>When I'm not building AI systems, you'll find me exploring new technologies, contributing to open source, or sharing knowledge through writing and speaking.</p>
                <div className="mt-4 flex flex-wrap gap-3 text-sm">
                  <Chip darkMode={darkMode}><BookOpen size={14}/> Technical Writing</Chip>
                  <Chip darkMode={darkMode}><Coffee size={14}/> Coffee Enthusiast</Chip>
                  <Chip darkMode={darkMode}><MapPin size={14}/> Remote & Travel</Chip>
                </div>
              </div>
              <div className={`${darkMode ? 'bg-white/5 text-white border-white/10' : 'bg-white text-gray-900 border-gray-200'} rounded-2xl p-6 border`}>
                <h4 className="font-semibold mb-3">Current Interests</h4>
                <ul className={`${darkMode ? 'text-white/80' : 'text-gray-700'} space-y-2 text-sm`}>
                  <li>• Multi-agent system orchestration</li>
                  <li>• AI safety and alignment research</li>
                  <li>• Distributed system architectures</li>
                  <li>• Open source AI tooling</li>
                </ul>
                <Magnetic>
                  <a href="/contact" className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white shadow hover:opacity-95 transition focus:outline-none focus-visible:ring-2 ${darkMode ? 'focus-visible:ring-cyan-300/60' : 'focus-visible:ring-indigo-500'}`}>Let's Connect <ArrowRight size={14}/></a>
                </Magnetic>
              </div>
            </div>
          </TiltCard>
        </section>
      </main>

      <footer className={`py-10 text-sm ${darkMode ? 'text-white/60' : 'text-gray-600'} border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">© {new Date().getFullYear()} Sikandar Tareen</div>
      </footer>

      {/* Theme toggle */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg border transition focus:outline-none focus:ring-2 ${darkMode ? 'border-white/20 bg-[#1a1c20] text-white hover:opacity-90 focus:ring-cyan-300/60' : 'border-gray-300 bg-white/95 backdrop-blur text-gray-800 hover:opacity-90 focus:ring-indigo-500'}`}
          aria-pressed={darkMode}
          aria-label="Toggle theme"
        >
          {darkMode ? <Sun size={16} /> : <Moon size={16} />}
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>

      {/* Reduce motion support */}
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>
    </div>
  );
}

/* ----------------------- Helpers ----------------------- */

function Chip({ children, darkMode }) {
  return (
    <div className={`${darkMode ? 'bg-white/10 border-white/15 text-white' : 'bg-white border-gray-200 text-gray-900'} inline-flex items-center gap-2 px-3 py-1.5 rounded-full border`}>{children}</div>
  );
}

/** TiltCard: 3D tilt on hover with theme-aware glow */
function TiltCard({ children, className = '', glow = false, darkMode = false }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const tx = useSpring(rx, { stiffness: 150, damping: 15 });
  const ty = useSpring(ry, { stiffness: 150, damping: 15 });

  function onMove(e) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0..1
    const py = (e.clientY - rect.top) / rect.height; // 0..1
    const ROT = 8; // degrees
    rx.set((0.5 - py) * ROT);
    ry.set((px - 0.5) * ROT);
    // propagate mouse position to CSS vars for glow
    el.style.setProperty('--mx', `${Math.round(px * 100)}%`);
    el.style.setProperty('--my', `${Math.round(py * 100)}%`);
  }
  function onLeave() { rx.set(0); ry.set(0); }

  const glowColor = darkMode ? 'rgba(34,211,238,0.28)' : 'rgba(99,102,241,0.25)';

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: tx, rotateY: ty, transformStyle: 'preserve-3d' }}
      className={`group relative will-change-transform transition-shadow hover:-translate-y-0.5 ${className}`}
    >
      {glow && (
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] blur-2xl opacity-0 group-hover:opacity-40 transition-opacity" style={{
          background: `radial-gradient(520px circle at var(--mx,50%) var(--my,50%), ${glowColor}, transparent 45%)`
        }} />
      )}
      {children}
    </motion.div>
  );
}

/** Magnetic: slight translation toward cursor on hover */
function Magnetic({ children }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 120, damping: 10 });
  const sy = useSpring(y, { stiffness: 120, damping: 10 });

  function onMove(e) {
    const el = ref.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * 10);
  }
  function onLeave() { x.set(0); y.set(0); }

  return (
    <motion.div ref={ref} onMouseMove={onMove} onMouseLeave={onLeave} style={{ x: sx, y: sy }} className="inline-block">
      {children}
    </motion.div>
  );
}

/** Parallax field: subtle floating sparkles */
function ParallaxField({ darkMode }) {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className={`absolute left-1/4 top-10 w-40 h-40 rounded-full ${darkMode ? 'bg-white/5' : 'bg-indigo-100'} blur-3xl animate-[float_10s_ease-in-out_infinite]`} />
      <div className={`absolute right-10 top-1/3 w-24 h-24 rounded-full ${darkMode ? 'bg-indigo-600/20' : 'bg-indigo-200/70'} blur-2xl animate-[float_12s_ease-in-out_infinite]`} />
      <div className={`absolute left-10 bottom-10 w-24 h-24 rounded-full ${darkMode ? 'bg-cyan-500/20' : 'bg-cyan-200/70'} blur-2xl animate-[float_14s_ease-in-out_infinite]`} />
      <style>{`@keyframes float { 0%,100%{ transform: translateY(0)} 50%{ transform: translateY(-12px)} }`}</style>
    </div>
  );
}